import { useEffect, useRef, useCallback } from 'react';
import echo from '../services/echo';
import type { TableRealtimeHandlers } from './useTableRealtimeListener';

/**
 * WebSocket bağlantısı kesildiğinde otomatik polling fallback'i olan
 * masa realtime dinleyici hook.
 *
 * Davranış:
 *   - WS bağlı      → Push eventleri ile local state güncellenir, polling yok.
 *   - WS kesildi     → 10 saniye arayla GET /api/tables polling.
 *   - WS yeniden bağlandı → Polling durdurulur, tam state çekilir (reconciliation).
 *
 * ═══ KÖHNƏLMİŞ CALLBACK TƏLƏSİ (düzəldildi) ═══
 *
 * Effekt yalnız `restaurantId`-dən asılıdır — kanal hər render-də yenidən
 * qurulmasın deyə. Amma `handlers` və `fallbackFetch` birbaşa istifadə
 * olunanda İLK RENDER-dəki nüsxə əbədi qalırdı.
 *
 * Nəticə real nasazlıq idi: `Masalar.tsx` fallback kimi
 * `() => fetchTables(masaType, { silent: true })` verir. Köhnə closure
 * mount anındakı `masaType`-i saxlayırdı, adətən 0 = "Hamısı". Hər 10
 * saniyəlik polling BÜTÜN masaları çəkib ekrandakı qrupun üzərinə yazırdı:
 *
 *   ofisant "Salon"a basır → salon masaları gəlir
 *   10 saniyə sonra polling işləyir → bütün masalar (teras daxil) görünür
 *   "Teras"a keçir → düzgün gəlir → yenə polling → yenə qarışır
 *
 * WS qopanda polling işlədiyi üçün bu, ƏN ÇOX OFLAYN rejimdə baş verirdi.
 * Eyni köhnə closure `connected` hadisəsində də çağırılırdı — yəni internet
 * qayıdan anda da qarışırdı.
 *
 * Həll: callback-lar ref-də saxlanır, hər render-də yenilənir; effekt isə
 * `ref.current` oxuyur. Kanal bir dəfə qurulur, çağırışlar həmişə təzədir.
 *
 * @param restaurantId   - Aktif restoran ID'si
 * @param handlers       - Event handler callback'leri
 * @param fallbackFetch  - Polling/reconnect sırasında çağrılacak tam fetch fonksiyonu
 */
export function useTableRealtimeWithFallback(
    restaurantId: number | string | null,
    handlers: TableRealtimeHandlers = {},
    fallbackFetch: () => void
): void {
    const isConnected = useRef(false);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // Hər render-də ən son nüsxə. Effekt bunlardan oxuyur.
    const handlersRef = useRef(handlers);
    handlersRef.current = handlers;
    const fallbackFetchRef = useRef(fallbackFetch);
    fallbackFetchRef.current = fallbackFetch;

    // Polling'i başlat
    const startPolling = useCallback(() => {
        if (intervalRef.current) return;
        intervalRef.current = setInterval(() => {
            fallbackFetchRef.current();
        }, 10000); // 10s — önceki polling süresiyle aynı
    }, []);

    // Polling'i durdur
    const stopPolling = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, []);

    useEffect(() => {
        if (!restaurantId) return;

        const channelName = `restaurant.${restaurantId}.tables`;
        const channel = echo.channel(channelName);

        // Bağlantı durumunu izle
        const connector = (echo as any).connector?.pusher;

        if (connector?.connection) {
            connector.connection.bind('connected', () => {
                isConnected.current = true;
                stopPolling();
                // Reconciliation — yeniden bağlandığında tam state çek
                fallbackFetchRef.current();
            });

            connector.connection.bind('disconnected', () => {
                isConnected.current = false;
                startPolling();
            });

            // Şu an zaten bağlıysa polling başlatma
            if (connector.connection.state === 'connected') {
                isConnected.current = true;
            } else {
                // WS bağlanana kadar polling başlat
                startPolling();
            }
        } else {
            // Connector erişilemezse fallback olarak polling başlat
            startPolling();
        }

        // Event listener'ları kaydet.
        //
        // Handler-lər ref üzərindən çağırılır — birbaşa `handlers.onX` versək
        // kanal ilk render-dəki funksiyanı saxlayardı və o da öz növbəsində
        // köhnə `masaType`/`tables` dəyərlərini görərdi.
        channel.listen('.table.status-changed', (data: any) =>
            handlersRef.current.onStatusChanged?.(data),
        );
        channel.listen('.table.created', (data: any) =>
            handlersRef.current.onTableCreated?.(data),
        );
        channel.listen('.table.updated', (data: any) =>
            handlersRef.current.onTableUpdated?.(data),
        );
        channel.listen('.table.deleted', (data: any) =>
            handlersRef.current.onTableDeleted?.(data),
        );
        channel.listen('.table.transferred', (data: any) =>
            handlersRef.current.onTableTransferred?.(data),
        );

        return () => {
            echo.leaveChannel(channelName);
            stopPolling();
            if (connector?.connection) {
                connector.connection.unbind('connected');
                connector.connection.unbind('disconnected');
            }
        };
    }, [restaurantId, startPolling, stopPolling]);
}
